import { useCallback, useEffect, useRef } from 'react';

// Fraction of the panel height a drag must cover to dismiss on release.
const DISMISS_DISTANCE_RATIO = 0.25;
// Release speed (px/ms) that dismisses regardless of distance, so a short flick works.
const DISMISS_VELOCITY = 0.5;
// Movement needed before the gesture commits to an axis.
const AXIS_LOCK_THRESHOLD = 8;
// Divisor applied to travel past the closed edge, so over-dragging feels bounded.
const OVERDRAG_DAMPING = 3;
const SETTLE_DURATION_MS = 200;
// How long the release events of a finished drag stay suppressed.
const RELEASE_SUPPRESSION_MS = 400;

type SwipeState = {
  pointerId: number;
  startX: number;
  startY: number;
  startTime: number;
  committed: boolean;
  offset: number;
};

/**
 * Decides whether a drag starting on `target` belongs to the sheet or to the
 * content underneath it. A drag inside a scroller that still has room to scroll
 * towards the closed edge belongs to the scroller, not the sheet.
 */
const isEligibleOrigin = (target: HTMLElement, panel: HTMLElement) => {
  if (target.closest('[data-drawer-no-swipe]')) return false;
  if (target.closest('[data-drawer-drag-handle]')) return true;

  let node: HTMLElement | null = target;
  while (node && node !== panel) {
    const { overflowY } = window.getComputedStyle(node);
    if (/(auto|scroll|overlay)/.test(overflowY) && node.scrollHeight > node.clientHeight) {
      return node.scrollTop <= 0;
    }
    node = node.parentElement;
  }

  return true;
};

type UseSwipeToDismissOptions = {
  enabled: boolean;
  onDismiss?: () => void;
};

/**
 * Drag-to-dismiss for a bottom sheet: the panel tracks the pointer, then either
 * settles back or animates out and closes.
 */
const useSwipeToDismiss = ({ enabled, onDismiss }: UseSwipeToDismissOptions) => {
  const panelRef = useRef<HTMLElement | null>(null);
  const stateRef = useRef<SwipeState | null>(null);
  const releaseCleanupRef = useRef<(() => void) | null>(null);

  /**
   * Swallow the mouseup/click that ends a drag. Dragging translates the panel, so
   * the release lands over whatever now sits under the pointer — which the
   * outside-click close reads as a click outside, and which may be an unrelated
   * button. Neither should react to the end of a gesture.
   */
  const suppressRelease = useCallback(() => {
    releaseCleanupRef.current?.();

    const swallow = (event: Event) => {
      event.stopImmediatePropagation();
      event.preventDefault();
      cleanup();
    };
    const cleanup = () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('mouseup', swallow, true);
      document.removeEventListener('click', swallow, true);
      releaseCleanupRef.current = null;
    };

    const timeoutId = window.setTimeout(cleanup, RELEASE_SUPPRESSION_MS);
    document.addEventListener('mouseup', swallow, true);
    document.addEventListener('click', swallow, true);
    releaseCleanupRef.current = cleanup;
  }, []);

  const setPanelTransform = useCallback((offset: number | null, durationMs = 0) => {
    const panel = panelRef.current;
    if (!panel) return;

    panel.style.transition = durationMs ? `transform ${durationMs}ms ease-out` : '';
    panel.style.transform = offset === null ? '' : `translateY(${offset}px)`;
  }, []);

  const clearPanelTransform = useCallback(() => {
    const panel = panelRef.current;
    if (!panel) return;

    panel.style.transition = '';
    panel.style.transform = '';
  }, []);

  // Never leave an inline transform behind — it would win over the enter/leave
  // transition classes and freeze the panel mid-screen.
  useEffect(
    () => () => {
      clearPanelTransform();
      releaseCleanupRef.current?.();
    },
    [clearPanelTransform],
  );

  const reset = useCallback((event?: React.PointerEvent<HTMLElement>) => {
    const state = stateRef.current;
    stateRef.current = null;
    if (!state || !event) return;

    if (event.currentTarget.hasPointerCapture?.(state.pointerId)) {
      event.currentTarget.releasePointerCapture(state.pointerId);
    }
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!enabled || stateRef.current) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (!isEligibleOrigin(event.target as HTMLElement, event.currentTarget)) return;

      stateRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startTime: Date.now(),
        committed: false,
        offset: 0,
      };
    },
    [enabled],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const state = stateRef.current;
      if (!state || state.pointerId !== event.pointerId) return;

      const deltaX = event.clientX - state.startX;
      const deltaY = event.clientY - state.startY;

      if (!state.committed) {
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < AXIS_LOCK_THRESHOLD) return;

        // Hand mostly-horizontal or upward gestures back to whatever is underneath
        // (carousels, scrollers) and stay out of the way for the rest of the drag.
        if (Math.abs(deltaX) >= Math.abs(deltaY) || deltaY <= 0) {
          reset(event);
          return;
        }

        state.committed = true;
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }

      state.offset = deltaY > 0 ? deltaY : deltaY / OVERDRAG_DAMPING;
      setPanelTransform(state.offset);
    },
    [reset, setPanelTransform],
  );

  const onPointerUp = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const state = stateRef.current;
      if (!state || state.pointerId !== event.pointerId) return;

      const { committed, offset, startTime } = state;
      reset(event);
      if (!committed) return;

      suppressRelease();

      const panelHeight = event.currentTarget.getBoundingClientRect().height || 1;
      const elapsed = Math.max(Date.now() - startTime, 1);
      const shouldDismiss = offset > panelHeight * DISMISS_DISTANCE_RATIO || offset / elapsed > DISMISS_VELOCITY;

      if (!shouldDismiss) {
        setPanelTransform(0, SETTLE_DURATION_MS);
        window.setTimeout(clearPanelTransform, SETTLE_DURATION_MS);
        return;
      }

      // Finish the exit ourselves, then close: the inline transform outranks the
      // leave-transition classes, so handing over mid-drag would look like a jump.
      setPanelTransform(panelHeight, SETTLE_DURATION_MS);
      window.setTimeout(() => {
        clearPanelTransform();
        onDismiss?.();
      }, SETTLE_DURATION_MS);
    },
    [clearPanelTransform, onDismiss, reset, setPanelTransform, suppressRelease],
  );

  const onPointerCancel = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const wasCommitted = stateRef.current?.committed;
      reset(event);
      if (!wasCommitted) return;

      suppressRelease();
      clearPanelTransform();
    },
    [clearPanelTransform, reset, suppressRelease],
  );

  return {
    panelRef,
    swipeHandlers: enabled ? { onPointerDown, onPointerMove, onPointerUp, onPointerCancel } : {},
  };
};

export default useSwipeToDismiss;
